import React, { useEffect, useState } from "react";
import { Badge, Button, Divider, Grid, Group, Loader, Modal, Paper, Pill, ScrollArea, Stack, Table, Text, Title } from "@mantine/core";
import { Timestamp, doc, getDoc } from "firebase/firestore";
import { notifications } from "@mantine/notifications";
import { IconFileSpreadsheet } from "@tabler/icons-react";
import { Campaign, CampaignError, CampaignTypeBase, Customer } from "../../types";
import { useFirestore } from "../../helpers/firebaseContext";
import { exportCampaignRecipientsExcel } from "./export_campaigns_excel";
import { getCampaignRecipientCount, getCampaignRecipientDisplay } from "./campaign_recipients";

export const CAMPAIGN_CHANNELS = [
  { key: "email", label: "Email", color: "gray" },
  { key: "custom_email", label: "Custom Email", color: "blue" },
  { key: "sms", label: "SMS", color: "pink" },
  { key: "whatsapp", label: "Whatsapp", color: "green" },
  { key: "notification", label: "Notification", color: "yellow" },
] as const;

export type CampaignChannelKey = (typeof CAMPAIGN_CHANNELS)[number]["key"];

export const getCampaignChannel = (campaign: Campaign, key: CampaignChannelKey): CampaignTypeBase | undefined =>
  campaign[key] as CampaignTypeBase | undefined;

export const formatCampaignTimestamp = (value?: Timestamp | null) => {
  if (!value || typeof value.toDate !== "function") {
    return "-";
  }
  const date = value.toDate();
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("en-gb");
};

export const campaignStatusLabel = (status?: string) =>
  status === "waiting_for_approve" ? "Waiting for approval" : status === "done" ? "Done" : status ?? "-";

const DetailItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed" tt="uppercase">
      {label}
    </Text>
    {typeof value === "string" || typeof value === "number" || value === undefined || value === null
      ? <Text size="sm">{value ?? "-"}</Text>
      : value}
  </Stack>
);

const MessageBlock = ({ label, content }: { label: string; content?: string | null }) => {
  if (!content) {
    return null;
  }
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Paper withBorder p="sm" bg="var(--mantine-color-gray-0)">
        <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {content}
        </Text>
      </Paper>
    </Stack>
  );
};

const CampaignDetailsModal = ({
  campaignId,
  onClose,
}: {
  campaignId: string | null;
  onClose: () => void;
}) => {
  const db = useFirestore();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [recipientDisplay, setRecipientDisplay] = useState<{ campaignId: string; mobile?: string } | null>(null);

  async function exportReport() {
    if (!campaign) {
      return;
    }
    try {
      setExporting(true);
      const count = await exportCampaignRecipientsExcel(db, campaign);
      notifications.show({
        position: "top-right",
        color: "green",
        title: "Report downloaded",
        message: `Exported ${count} recipients to Excel`,
      });
    } catch (exportError: any) {
      notifications.show({
        position: "top-right",
        color: "red",
        title: "Failed to export report",
        message: exportError?.message,
      });
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    if (!campaignId) {
      setCampaign(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getDoc(doc(db, "campaigns", campaignId))
      .then((snapshot) => {
        if (cancelled) {
          return;
        }
        if (!snapshot.exists()) {
          setError("Campaign not found.");
          setCampaign(null);
        } else {
          setCampaign({ ...(snapshot.data() as Campaign), id: snapshot.id });
        }
      })
      .catch((fetchError: any) => {
        if (!cancelled) {
          setError(fetchError?.message ?? "Failed to load campaign.");
          setCampaign(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId, db]);

  useEffect(() => {
    if (!campaign) return;
    let cancelled = false;
    getCampaignRecipientDisplay(campaign, async (id) => {
      const snapshot = await getDoc(doc(db, "customers", id));
      return snapshot.exists() ? snapshot.data() as Customer : undefined;
    }).then(({ mobile }) => {
      if (!cancelled) setRecipientDisplay({ campaignId: campaign.id, mobile });
    });
    return () => { cancelled = true; };
  }, [campaign, db]);

  const activeChannels = campaign
    ? CAMPAIGN_CHANNELS.filter(({ key }) => getCampaignChannel(campaign, key))
    : [];

  const errorsByChannel: { label: string; errors: CampaignError[] }[] = campaign
    ? CAMPAIGN_CHANNELS
      .map(({ key, label }) => ({ label, errors: getCampaignChannel(campaign, key)?.errors ?? [] }))
      .filter(({ errors }) => errors.length > 0)
    : [];

  const totals = activeChannels.reduce(
    (sums, { key }) => {
      const channel = getCampaignChannel(campaign!, key)!;
      sums.total += channel.message_total ?? 0;
      sums.sent += channel.message_sent ?? 0;
      sums.failed += channel.message_failed ?? 0;
      return sums;
    },
    { total: 0, sent: 0, failed: 0 }
  );

  const recipientsCount = campaign ? getCampaignRecipientCount(campaign) : 0;
  const recipientMobile = recipientDisplay?.campaignId === campaign?.id ? recipientDisplay?.mobile : undefined;

  const whatsappTemplate = campaign?.whatsapp?.template ?? campaign?.selectedWhatsappTemplate;
  const whatsappBody = whatsappTemplate?.types
    ? Object.values(whatsappTemplate.types)[0]?.body
    : undefined;

  return (
    <Modal
      opened={!!campaignId}
      onClose={onClose}
      size="xl"
      title={<Title order={3}>{campaign?.title ?? "Campaign Details"}</Title>}
    >
      {loading && (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      )}

      {!loading && error && (
        <Text c="red" py="md">
          {error}
        </Text>
      )}

      {!loading && !error && campaign && (
        <Stack gap="md">
          <Group justify="flex-end">
            <Button
              variant="outline"
              size="xs"
              leftSection={<IconFileSpreadsheet size={16} />}
              loading={exporting}
              onClick={exportReport}
            >
              Excel Report
            </Button>
          </Group>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <DetailItem
                label="Status"
                value={(
                  <Badge size="sm" variant="filled" color={campaign.status === "done" ? "green" : "gray"}>
                    {campaignStatusLabel(campaign.status)}
                  </Badge>
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <DetailItem
                label="Channels"
                value={(
                  <Group gap={4}>
                    {CAMPAIGN_CHANNELS
                      .filter(({ key }) => campaign.type?.includes(key === "custom_email" ? "customEmail" : key) || getCampaignChannel(campaign, key))
                      .map(({ key, label, color }) => (
                        <Badge key={key} size="xs" variant="filled" color={color}>
                          {label}
                        </Badge>
                      ))}
                  </Group>
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <DetailItem label="Created By" value={campaign.created_by} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <DetailItem label="Created On" value={formatCampaignTimestamp(campaign.created_on)} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              {recipientMobile ? (
                <DetailItem
                  label="Mobile Number"
                  value={(
                    <Text size="sm" dir="ltr" style={{ overflowWrap: "anywhere" }}>
                      {recipientMobile}
                    </Text>
                  )}
                />
              ) : <DetailItem label="Recipients" value={recipientsCount} />}
            </Grid.Col>
            {campaign.run_by && (
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <DetailItem label="Run By" value={campaign.run_by} />
              </Grid.Col>
            )}
            {campaign.run_on && (
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <DetailItem label="Run On" value={formatCampaignTimestamp(campaign.run_on)} />
              </Grid.Col>
            )}
          </Grid>

          {(campaign.tagsRecipients?.length || campaign.segmentsRecipients?.length || campaign.rmRecipients?.length) ? (
            <>
              <Divider label="Audience" labelPosition="left" />
              <Grid>
                {!!campaign.tagsRecipients?.length && (
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <DetailItem
                      label="Tags"
                      value={(
                        <Group gap={4}>
                          {campaign.tagsRecipients.map((tag) => <Pill key={tag}>{tag}</Pill>)}
                        </Group>
                      )}
                    />
                  </Grid.Col>
                )}
                {!!campaign.segmentsRecipients?.length && (
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <DetailItem
                      label="Segments"
                      value={(
                        <Group gap={4}>
                          {campaign.segmentsRecipients.map((segment) => <Pill key={segment}>{segment}</Pill>)}
                        </Group>
                      )}
                    />
                  </Grid.Col>
                )}
                {!!campaign.rmRecipients?.length && (
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <DetailItem
                      label="RMs"
                      value={(
                        <Group gap={4}>
                          {campaign.rmRecipients.map((rm) => <Pill key={rm}>{rm}</Pill>)}
                        </Group>
                      )}
                    />
                  </Grid.Col>
                )}
              </Grid>
            </>
          ) : null}

          <Divider label="Message Content" labelPosition="left" />
          <Stack gap="sm">
            <MessageBlock label="SMS Message" content={campaign.sms?.message ?? campaign.smsMessage} />
            <MessageBlock label="Email Template" content={campaign.email?.template ?? campaign.selectedEmailTemplate} />
            <MessageBlock
              label="Custom Email Subject"
              content={campaign.custom_email?.subject ?? campaign.emailSubject}
            />
            <MessageBlock
              label="Custom Email Message"
              content={campaign.custom_email?.message ?? campaign.emailMessage}
            />
            <MessageBlock label="Whatsapp Template" content={whatsappTemplate?.friendly_name} />
            <MessageBlock label="Whatsapp Body" content={whatsappBody} />
            <MessageBlock
              label="Notification Title"
              content={campaign.notification?.title ?? campaign.notificationTitle}
            />
            <MessageBlock
              label="Notification Body"
              content={campaign.notification?.body ?? campaign.notificationBody}
            />
          </Stack>

          {activeChannels.length > 0 && (
            <>
              <Divider label="Delivery" labelPosition="left" />
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Channel</Table.Th>
                    <Table.Th>Total</Table.Th>
                    <Table.Th>Sent</Table.Th>
                    <Table.Th>Failed</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {activeChannels.map(({ key, label }) => {
                    const channel = getCampaignChannel(campaign, key)!;
                    return (
                      <Table.Tr key={key}>
                        <Table.Td>{label}</Table.Td>
                        <Table.Td>{channel.message_total ?? 0}</Table.Td>
                        <Table.Td>{channel.message_sent ?? 0}</Table.Td>
                        <Table.Td>{channel.message_failed ?? 0}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                  <Table.Tr fw={700}>
                    <Table.Td>Total</Table.Td>
                    <Table.Td>{totals.total}</Table.Td>
                    <Table.Td>{totals.sent}</Table.Td>
                    <Table.Td>{totals.failed}</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </>
          )}

          {errorsByChannel.length > 0 && (
            <>
              <Divider label="Errors" labelPosition="left" />
              <ScrollArea.Autosize mah={300}>
                <Stack gap="sm">
                  {errorsByChannel.map(({ label, errors }) => (
                    <Stack key={label} gap={4}>
                      <Text size="sm" fw={700}>
                        {label} Errors ({errors.length})
                      </Text>
                      {errors.map((channelError, index) => (
                        <Text key={index} size="sm">
                          <span style={{ fontWeight: "bold" }}>{channelError.customer_id} : </span>
                          {channelError.message}
                        </Text>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
};

export default CampaignDetailsModal;
